// ═══════════════════════════════════════════════════════════════════════════════
// Receipt photo thumbnail + full-screen viewer (keyboard / backdrop close)
// Saved receipts: authed API → blob URL (does not put GCS URL in img src)
// ═══════════════════════════════════════════════════════════════════════════════

import { useState, type KeyboardEvent } from 'react';
import {
  useAuthedApiImageBlobUrl,
  useAuthedReceiptImageBlobUrl,
  usePairingScopedImageBlobUrl,
} from './useReceiptImageDisplay';
import { FullscreenPinchImageViewer } from '../ui/FullscreenPinchImageViewer';

const EXPIRED_IMAGE_FALLBACK_SRC = '/img/img_expired-1.webp';

export type ReceiptImageLightboxProps = {
  alt: string;
  enlargeAriaLabel: string;
  closeLabel: string;
  /** Overrides default thumbnail button layout (e.g. grid tile `h-full w-full rounded-lg`) */
  thumbButtonClassName?: string;
  /** Overrides default thumbnail `<img>` classes */
  thumbImgClassName?: string;
} & (
  | {
      receiptId: string;
      previewSrc?: never;
      taskInstancePhoto?: never;
      inventoryV2LocationPhoto?: never;
      inventoryStocktakePhoto?: never;
    }
  | {
      previewSrc: string;
      receiptId?: never;
      taskInstancePhoto?: never;
      inventoryV2LocationPhoto?: never;
      inventoryStocktakePhoto?: never;
    }
  | {
      taskInstancePhoto: { taskInstanceId: string; photoIndex: number };
      receiptId?: never;
      previewSrc?: never;
      inventoryV2LocationPhoto?: never;
      inventoryStocktakePhoto?: never;
    }
  | {
      inventoryV2LocationPhoto: { taskInstanceId: string; locationIndex: number; photoIndex: number };
      receiptId?: never;
      previewSrc?: never;
      taskInstancePhoto?: never;
      inventoryStocktakePhoto?: never;
    }
  | {
      inventoryStocktakePhoto: { taskInstanceId: string; photoIndex: number };
      receiptId?: never;
      previewSrc?: never;
      taskInstancePhoto?: never;
      inventoryV2LocationPhoto?: never;
    }
);

export const ReceiptImageLightbox = (props: ReceiptImageLightboxProps) => {
  const { alt, enlargeAriaLabel, closeLabel, thumbButtonClassName, thumbImgClassName } = props;
  const [open, setOpen] = useState(false);

  const receiptId = 'receiptId' in props ? props.receiptId : undefined;
  const previewSrc = 'previewSrc' in props ? props.previewSrc : undefined;
  const taskPhoto = 'taskInstancePhoto' in props ? props.taskInstancePhoto : undefined;
  const inventoryV2Loc = 'inventoryV2LocationPhoto' in props ? props.inventoryV2LocationPhoto : undefined;
  const inventoryStock = 'inventoryStocktakePhoto' in props ? props.inventoryStocktakePhoto : undefined;

  const taskPhotoApiPath =
    taskPhoto != null
      ? `/api/task-instances/${encodeURIComponent(taskPhoto.taskInstanceId)}/photos/${taskPhoto.photoIndex}`
      : undefined;

  const inventoryPhotoApiPath =
    inventoryV2Loc != null
      ? `/api/task-instances/${encodeURIComponent(inventoryV2Loc.taskInstanceId)}/inventory/v2/locations/${inventoryV2Loc.locationIndex}/photos/${inventoryV2Loc.photoIndex}`
      : inventoryStock != null
        ? `/api/task-instances/${encodeURIComponent(inventoryStock.taskInstanceId)}/inventory/stocktake-photos/${inventoryStock.photoIndex}`
        : undefined;

  const authedBlobPath = taskPhoto != null ? taskPhotoApiPath : inventoryPhotoApiPath;

  const saved = useAuthedReceiptImageBlobUrl(receiptId);
  const preview = usePairingScopedImageBlobUrl(previewSrc);
  const authedBlob = useAuthedApiImageBlobUrl(authedBlobPath);

  const rawSrc =
    receiptId != null
      ? saved.objectUrl
      : previewSrc != null
        ? (preview.displaySrc ?? null)
        : authedBlobPath != null
          ? authedBlob.objectUrl
          : null;
  const displaySrc = rawSrc ?? EXPIRED_IMAGE_FALLBACK_SRC;
  const loading =
    receiptId != null
      ? saved.loading
      : previewSrc != null
        ? preview.loading
        : authedBlobPath != null
          ? authedBlob.loading
          : false;

  const defaultThumbBtnClass =
    'relative block w-full overflow-hidden rounded-lg border border-gray-200 bg-gray-100 text-left focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800';
  const defaultThumbImgClass = 'max-h-56 w-full object-contain';
  const resolvedThumbBtnClass = thumbButtonClassName ?? defaultThumbBtnClass;
  const resolvedThumbImgClass = thumbImgClassName ?? defaultThumbImgClass;

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleThumbKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleOpen();
    }
  };

  if (loading) {
    return (
      <div
        className="flex min-h-32 items-center justify-center rounded-lg border border-gray-200 bg-gray-100 dark:border-gray-600 dark:bg-gray-800"
        role="status"
        aria-live="polite"
      >
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600" />
      </div>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={handleOpen}
        onKeyDown={handleThumbKeyDown}
        className={resolvedThumbBtnClass}
        aria-label={enlargeAriaLabel}
        tabIndex={0}
      >
        <img src={displaySrc} alt={alt} className={resolvedThumbImgClass} />
        <span
          className="pointer-events-none absolute bottom-2 right-2 inline-flex h-8 w-8 items-center justify-center rounded-full bg-black/65 text-white shadow-sm"
          aria-hidden
        >
          <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2}>
            <circle cx="11" cy="11" r="7" />
            <path d="M20 20L16.65 16.65" strokeLinecap="round" />
          </svg>
        </span>
      </button>

      <FullscreenPinchImageViewer
        open={open}
        onClose={handleClose}
        src={displaySrc}
        alt={alt}
        dialogAriaLabel={enlargeAriaLabel}
        closeLabel={closeLabel}
      />
    </>
  );
};
